// Stevens Institute of Technology: public college page (#2839), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Stevens' Common Data Set 2025-2026 (CDS-2025-2026-PDF_Final.pdf, on
//    assets.stevens.edu), the newest edition the Office of Institutional
//    Research and Effectiveness links from its Facts and Figures page
//    (2022-2023 to 2025-2026 are posted). C1 applicants, admits and
//    enrollees for Fall 2025 with the residency table, C8 test policy for
//    Fall 2027, C21 early decision counts (Fall 2025), C22 early action
//    dates, G1/G5 costs (the file gives 2026-2027 figures). Quirk: the PDF's
//    embedded fonts do not extract as text, so the figures were read from
//    the rendered pages. The CDS budget matches the Office of Financial
//    Aid's 2026-2027 on-campus cost of attendance ($91,294 total; tuition
//    and fees $68,230; housing and meals $20,744; books $1,200; loan fees
//    $70 and miscellaneous $1,050, which the CDS reports together as
//    $1,120 of other expenses).
//  - Stevens Undergraduate Admissions: First-Year Applicants (requirements,
//    test policy, recommended coursework), First-Year Application Plans
//    (Early Decision terms), the Admissions Timeline (deadlines,
//    notification and deposit dates, the Accelerated Pre-Medicine
//    deadline), the Accelerated Law Program admission requirements, and
//    Portfolio Submission. The pages carry no cycle year; the test policy
//    names its own span (entry terms through Fall 2029). The CDS C21/C22
//    dates match the Admissions Timeline.
//  - College Scorecard, school id 186867 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Stevens Common Data Set 2025-2026',
        url: 'https://www.stevens.edu/office-of-institutional-research-and-effectiveness/facts-and-figures',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Stevens Undergraduate Admissions, First-Year Applicants',
        url: 'https://www.stevens.edu/admission-aid/undergraduate-admissions/first-year-students',
        read: '2026-10-01',
    },
    plans: {
        label: 'Stevens Undergraduate Admissions, First-Year Application Plans',
        url: 'https://www.stevens.edu/admission-aid/undergraduate-admissions/first-year-application-plans',
        read: '2026-10-01',
    },
    timeline: {
        label: 'Stevens Undergraduate Admissions, Admissions Timeline',
        url: 'https://www.stevens.edu/admission-aid/undergraduate-admissions/admissions-timeline',
        read: '2026-10-01',
    },
    law: {
        label: 'Stevens Accelerated Law Program, Admission Requirements',
        url: 'https://www.stevens.edu/admission-aid/undergraduate-admissions/pre-professional-and-accelerated-programs/accelerated-law-program/admission-requirements',
        read: '2026-10-01',
    },
    portfolio: {
        label: 'Stevens Undergraduate Admissions, Portfolio Submission',
        url: 'https://www.stevens.edu/admission-aid/undergraduate-admissions/portfolio-submission',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?186867-Stevens-Institute-of-Technology',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'stevens-institute-of-technology',
    kbId: 'stevens_institute_of_technology',
    name: 'Stevens Institute of Technology',
    shortName: 'Stevens',
    location: 'Hoboken, NJ',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Stevens brand guide); decoration only.
    branding: {
        primary: '#A32638',
        secondary: '#9A989A',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/stevens_institute_of_technology_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 10499,
        admitted: 5321,
        enrolled: 1104,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 5234, admitted: 2775 },
            { group: 'Out-of-state', applied: 3881, admitted: 2031 },
            { group: 'International', applied: 1371, admitted: 515 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table (13 applicants of unknown residency, none admitted, not shown)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Stevens\' Common Data Set reports one first-year acceptance rate for the whole institute, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision (I and II together)', applied: 407, admitted: 284 },
        ],
        note: 'The Common Data Set gives one early decision count for both rounds. It gives the early action dates but no early action counts.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Most applicants follow the plans below. A few programs ask for more:',
        rows: [
            { school: 'Accelerated Pre-Medicine Program', requirement: 'A Regular Decision application with every material in by November 1. Test scores are required: a combined SAT of 1400 (reading and math) or an ACT composite (with science) of 32, in one sitting, taken before November 1 and sent by the testing agency. The program has its own interview requirement.', source: 'firstYear' },
            { school: 'Accelerated Pre-Law Program', requirement: 'Study in the School of Humanities, Arts and Social Sciences, with a minimum high school GPA of 3.7, a combined SAT of 1370 or an ACT composite of 29 sent by the testing agency, and an interview if Stevens invites you.', source: 'law' },
            { school: 'Music and Technology, Visual Arts and Technology', requirement: 'A portfolio is strongly encouraged, uploaded to your applicant portal after you apply, with the program\'s supplemental questions.', source: 'portfolio' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 5' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 15', decision: 'By December 15', binding: true },
            { plan: 'Early Action', deadline: 'December 1', decision: 'By February 1', binding: false },
            { plan: 'Early Decision II', deadline: 'January 5', decision: 'By February 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 5', decision: 'By April 1', binding: false },
        ],
        note: 'Deposits are due January 10 (Early Decision I), March 1 (Early Decision II) and May 1 (Early Action and Regular Decision). Early Action admits who deposit by March 5 get priority access to housing and fall course registration. Tests need to be taken a month before the application deadline, and interviews, optional for most applicants, finished by it.',
        source: 'timeline',
        binding: 'Early Decision is binding: you commit to attend Stevens if admitted, submit a non-refundable $500 tuition deposit, withdraw all other applications and send no new ones. You may apply Early Decision to only one school. You, a parent or guardian and your counselor sign the Early Decision Agreement.',
        bindingSource: 'plans',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Stevens is test-optional, with some exceptions, for first-year applicants for entry terms through fall 2029. If you choose to submit scores, you may self-report them on your application portal. The Accelerated Pre-Medicine and Pre-Law programs still require test scores.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for some fall 2027 applicants: the accelerated pre-medicine and pre-law programs.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    // The test-optional policy already runs through fall 2029 entry, so
    // fall 2028 has no announced change.
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 65530 },
            { item: 'Required fees', amount: 2700 },
            { item: 'Housing (on campus)', amount: 12214 },
            { item: 'Food (on-campus meal plan)', amount: 8530 },
            { item: 'Books and supplies', amount: 1200 },
            { item: 'Transportation', amount: 0 },
            { item: 'Other expenses', amount: 1120 },
        ],
        total: 91294,
        note: 'For a full-time first-year student living on campus, the full academic year, as the Common Data Set gives it (it lists no transportation cost for students living on campus). Stevens reports that tuition and fees do not vary by year of study or by program.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 37396,
            byIncome: [
                { income: '$0 to $30,000', amount: 28690 },
                { income: '$30,001 to $48,000', amount: 25224 },
                { income: '$48,001 to $75,000', amount: 30982 },
                { income: '$75,001 to $110,000', amount: 34052 },
                { income: 'Over $110,000', amount: 45333 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Stevens\' aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
